import { formatCount } from '@dbrain/app-core/format';
import { formatPlanTokens, formatTry, type MyPlanDto, type PlanLimitNoticeDto, type QuotaNoticeDto } from '@dbrain/shared';
import { Button, Dialog, Icons } from '@dbrain/ui';
import { useId } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { usePlan, usePlanUsage, useProPlan } from '../../api/plan.js';
import { settingsLink } from '../settings/links.js';
import { usePlanOffer } from './PlanOffer.js';

/**
 * Phase 16a (ADR-0023, 06 §28.4): the bireysel edition's plan on the screens (B2cTokenBitti, B2cGunlukBitti,
 * B2cDosyaBoyutu, B2cBelgeSiniri, B2cBaglantilar). Each part is drawn only where the edition has plans.
 */

/** "✦ Ücretsiz paket · Pro'ya geç" in the bar for Ücretsiz; Pro's badge stands next to the product's name instead. */
export function PlanChip() {
  const { t } = useTranslation();
  const plan = usePlan();
  const offer = usePlanOffer();
  if (!plan || plan.id === 'pro' || !offer.canOffer) return null;
  return (
    <button type="button" className="plan-chip" onClick={offer.open} aria-label={t('b2c.chip.label')} data-testid="plan-upgrade">
      <Icons.skills size={14} aria-hidden="true" />
      <span className="plan-chip__plan">{t('b2c.chip.free')}</span>
      <span aria-hidden="true">·</span>
      <span className="plan-chip__go">{t('b2c.chip.upgrade')}</span>
    </button>
  );
}

/** "Pro" on the right of the bars for a person on Pro, in the place of Ücretsiz's chip (B2cAbonelik, B2cGunlukBitti) */
export function ProBadge() {
  const { t } = useTranslation();
  const plan = usePlan();
  if (plan?.id !== 'pro') return null;
  return (
    <span className="plan-badge" aria-label={t('b2c.chip.proLabel')} data-testid="plan-badge">
      {t('b2c.chip.pro')}
    </span>
  );
}

/** "Pro'ya geç · 500 TL/ay" (the price when Pro is on sale) */
export function UpgradeButton({ testId = 'plan-card-upgrade' }: { testId?: string }) {
  const { t, i18n } = useTranslation();
  const pro = useProPlan();
  const offer = usePlanOffer();
  if (!offer.canOffer) return null;
  return (
    <Button icon={<Icons.skills size={15} />} onClick={offer.open} data-testid={testId}>
      {pro ? t('b2c.allowance.upgrade', { price: formatTry(pro.monthlyPriceKurus, i18n.language, true) }) : t('b2c.allowance.upgradePlain')}
    </Button>
  );
}

/** The day's use against the limit, as the card and Ayarlar › Abonelik draw it */
export function UsageBar({ used, limit, label }: { used: number; limit: number; label: string }) {
  const percent = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const tone = percent >= 100 ? 'danger' : percent >= 80 ? 'warning' : 'ok';
  return (
    <span className={`plan-bar plan-bar--${tone}`} role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={limit} aria-valuenow={Math.min(used, limit)}>
      <span className="plan-bar__fill" style={{ width: `${percent}%` }} />
    </span>
  );
}

/**
 * D-265: the question waits for the daily allowance (B2cTokenBitti for Ücretsiz, B2cGunlukBitti for Pro). The plan
 * job carries it on after the reset (or when Pro is given); an older one that went on is a quiet line.
 */
export function AllowanceCard({ quota, latest }: { quota: QuotaNoticeDto | null; latest: boolean }) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const location = useLocation();
  const plan = usePlan();
  const pro = useProPlan();
  const offer = usePlanOffer();
  if (!latest) {
    return (
      <div className="message message--notice notice--waited" data-testid="allowance-waited">
        <span>{t('b2c.allowance.waited')}</span>
      </div>
    );
  }
  const onPro = plan?.id === 'pro';
  const limit = quota?.limit ?? plan?.dailyTokens ?? 0;
  const used = quota?.used ?? limit;
  const words = { plan: plan?.name ?? '', limit: formatPlanTokens(limit, i18n.language), pro: pro ? formatPlanTokens(pro.dailyTokens, i18n.language) : '' };
  return (
    <section className="budget-notice allowance-card" role="status" aria-labelledby={titleId} data-testid="allowance-card" data-plan={plan?.id ?? ''}>
      <span className="budget-notice__icon" aria-hidden="true">
        <Icons.time size={18} />
      </span>
      <div className="budget-notice__body">
        <h2 id={titleId} className="budget-notice__title">
          {t(onPro ? 'b2c.allowance.proTitle' : 'b2c.allowance.freeTitle')}
        </h2>
        <p className="budget-notice__text">
          <Trans i18nKey={onPro ? 'b2c.allowance.proBody' : pro && offer.canOffer ? 'b2c.allowance.freeBody' : 'b2c.allowance.freeBodyNoOffer'} values={words} components={{ b: <strong /> }} />
        </p>
        <div className="allowance-card__use">
          <span className="allowance-card__today">{t('b2c.allowance.today')}</span>
          <span className="allowance-card__count" data-testid="allowance-use">
            <Trans i18nKey="b2c.allowance.use" values={{ used: formatPlanTokens(used, i18n.language), limit: formatPlanTokens(limit, i18n.language) }} components={{ b: <strong /> }} />
          </span>
        </div>
        <UsageBar used={used} limit={limit} label={t('b2c.allowance.useLabel', { used: formatPlanTokens(used, i18n.language), limit: formatPlanTokens(limit, i18n.language) })} />
        <p className="allowance-card__reset">{t(onPro ? 'b2c.allowance.resetPro' : 'b2c.allowance.resetFree')}</p>
        <div className="budget-notice__actions allowance-card__actions">
          {onPro || !offer.canOffer ? null : (
            <>
              <UpgradeButton />
              <Button variant="secondary" onClick={offer.compare} data-testid="plan-card-compare">
                {t('b2c.allowance.compare')}
              </Button>
            </>
          )}
          <Link className="allowance-card__usage" {...settingsLink('subscription', location)} data-testid="plan-card-usage">
            {t('b2c.allowance.usage')}
          </Link>
        </div>
      </div>
    </section>
  );
}

/** The composer's placeholder while a question waits for the allowance */
export function allowancePlaceholder(plan: MyPlanDto | null): 'b2c.allowance.placeholderPro' | 'b2c.allowance.placeholderFree' {
  return plan?.id === 'pro' ? 'b2c.allowance.placeholderPro' : 'b2c.allowance.placeholderFree';
}

const sizeText = (bytes: number, locale: string) => `${(Math.round((bytes / (1024 * 1024)) * 10) / 10).toLocaleString(locale, { maximumFractionDigits: 1 })} MB`;

/** B2cDosyaBoyutu: the picked file over the plan's size stays as a chip, marked; nothing is uploaded */
export function OversizeChip({ name, size, onRemove }: { name: string; size: number; onRemove: () => void }) {
  const { t, i18n } = useTranslation();
  return (
    <li className="chip attachment-chip composer-file composer-file--oversize" data-testid="oversize-chip">
      <span className="composer-file__kind" aria-hidden="true">
        {(name.split('.').pop() ?? '').slice(0, 4).toUpperCase()}
      </span>
      <span className="composer-file__name">{name}</span>
      <span className="composer-file__state composer-file__state--failed">{t('b2c.oversize.chip', { size: sizeText(size, i18n.language) })}</span>
      <button type="button" className="ui-icon-button ui-icon-button--sm" aria-label={t('b2c.oversize.remove', { name })} title={t('b2c.oversize.remove', { name })} onClick={onRemove} data-testid="oversize-remove">
        <Icons.close size={14} />
      </button>
    </li>
  );
}

/** The words under the composer for a file over the size (with "Pro'ya geç" on Ücretsiz) */
export function OversizeNotice({ size, limitMb }: { size: number; limitMb: number }) {
  const { t, i18n } = useTranslation();
  const plan = usePlan();
  const pro = useProPlan();
  const offer = usePlanOffer();
  const words = { size: sizeText(size, i18n.language), limit: limitMb, plan: plan?.name ?? '', pro: pro?.maxFileMb ?? '' };
  const key = plan?.id === 'pro' || !plan ? 'b2c.oversize.pro' : pro && offer.canOffer ? 'b2c.oversize.free' : 'b2c.oversize.freeNoOffer';
  return (
    <div className="oversize-notice" role="alert" data-testid="oversize-notice">
      <Icons.warning size={16} aria-hidden="true" />
      <span>{t(key, words)}</span>
      {plan && plan.id !== 'pro' && offer.canOffer ? (
        <button type="button" className="oversize-notice__upgrade" onClick={offer.open} data-testid="oversize-upgrade">
          {t('b2c.oversize.upgrade')}
        </button>
      ) : null}
    </div>
  );
}

/** B2cBelgeSiniri: the document limit reached (on upload, or when a chat attachment is kept) */
export function DocumentLimitDialog({ limit, name, onClose }: { limit: PlanLimitNoticeDto | null; name: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const plan = usePlan();
  const pro = useProPlan();
  const offer = usePlanOffer();
  const onPro = (limit?.plan ?? plan?.id) === 'pro';
  const max = limit?.limit ?? plan?.maxDocuments ?? 0;
  const words = { plan: plan?.name ?? '', limit: max.toLocaleString('tr-TR'), name: name ?? '' };
  return (
    <Dialog
      title={t('b2c.documents.limitTitle')}
      onClose={onClose}
      closeLabel={t('b2c.documents.close')}
      size="sm"
      role="alertdialog"
      data-testid="document-limit"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} data-testid="document-limit-manage">
            {t('b2c.documents.manage')}
          </Button>
          {onPro ? null : <UpgradeButton testId="document-limit-upgrade" />}
        </>
      }
    >
      <p>
        <Trans
          i18nKey={
            onPro ? 'b2c.documents.limitPro' : offer.canOffer ? (name ? 'b2c.documents.limitNamed' : 'b2c.documents.limitPlain') : name ? 'b2c.documents.limitNamedNoOffer' : 'b2c.documents.limitPlainNoOffer'
          }
          values={words}
          components={{ b: <strong /> }}
        />
      </p>
      {!onPro && pro && offer.canOffer ? (
        <p className="document-limit__pro">
          <Icons.skills size={15} aria-hidden="true" />
          {t('b2c.documents.proInfo', { limit: pro.maxDocuments.toLocaleString('tr-TR'), mb: pro.maxFileMb })}
        </p>
      ) : null}
    </Dialog>
  );
}

/** B2cBaglantilar: own connections are Pro's; the ones a person kept from Pro stay listed under it */
export function ConnectionsUpsell({ kept }: { kept: number }) {
  const { t } = useTranslation();
  const offer = usePlanOffer();
  const steps = ['address', 'signIn', 'use'] as const;
  return (
    <section className="connections-upsell" aria-labelledby="connections-upsell-title" data-testid="connections-upsell">
      <span className="connections-upsell__icon" aria-hidden="true">
        <Icons.plug size={18} />
      </span>
      <h2 id="connections-upsell-title" className="connections-upsell__title">
        {t('b2c.connections.title')}
      </h2>
      <p className="connections-upsell__body">{t('b2c.connections.body')}</p>
      <ol className="connections-upsell__steps">
        {steps.map((step, index) => (
          <li key={step}>
            <span className="connections-upsell__step" aria-hidden="true">
              {index + 1}
            </span>
            <span>
              <strong>{t(`b2c.connections.steps.${step}.title`)}</strong>
              <span className="connections-upsell__step-body">{t(`b2c.connections.steps.${step}.body`)}</span>
            </span>
          </li>
        ))}
      </ol>
      {offer.canOffer ? (
        <div className="connections-upsell__actions">
          <UpgradeButton testId="connections-upgrade" />
          <Button variant="secondary" onClick={offer.compare} data-testid="connections-compare">
            {t('b2c.allowance.compare')}
          </Button>
        </div>
      ) : null}
      {kept > 0 ? <p className="connections-upsell__kept">{t('b2c.connections.kept')}</p> : null}
      <p className="connections-upsell__secure">
        <Icons.lock size={14} aria-hidden="true" />
        {t('b2c.connections.secure')}
      </p>
    </section>
  );
}

/**
 * 16c.1 (B2cSkillSiniri): an own limit of the package where the edition has packages: "3 / 3 kendi skill'iniz" beside
 * the add button, and at the limit the note under the header (on Ücretsiz with "Pro'ya geç"). `limit` null: no limit,
 * and nothing is shown.
 */
export function useOwnLimit(kind: 'skills' | 'connections'): { used: number; limit: number | null; full: boolean } | null {
  const plan = usePlan();
  const usage = usePlanUsage({ enabled: plan !== null, refetchMs: 30_000 });
  if (!plan || !usage.data) return null;
  const own = kind === 'skills' ? usage.data.skills : usage.data.connections;
  return { used: own.count, limit: own.limit, full: own.limit !== null && own.count >= own.limit };
}

export function OwnLimitCount({ kind, used, limit }: { kind: 'skills' | 'connections'; used: number; limit: number }) {
  const { t, i18n } = useTranslation();
  return (
    <span className="own-limit__count" data-testid={`own-${kind}-count`}>
      <strong>{t(`b2cModels.${kind}.count`, { used: formatCount(used, i18n.language), limit: formatCount(limit, i18n.language) })}</strong> {t(`b2cModels.${kind}.countLabel`)}
    </span>
  );
}

export function OwnLimitNote({ kind, limit }: { kind: 'skills' | 'connections'; limit: number }) {
  const { t, i18n } = useTranslation();
  const plan = usePlan();
  const pro = useProPlan();
  const offer = usePlanOffer();
  const onFree = plan?.id === 'free' && offer.canOffer;
  const name = plan?.name ?? '';
  const value = formatCount(limit, i18n.language);
  // 16c.2 (B2cBaglantiSiniri), 16c.3 for skills: "Pro'da sınır yok" only while Pro has no limit of its own
  const proLimit = kind === 'connections' ? pro?.maxOwnConnections : pro?.maxOwnSkills;
  const body = onFree ? (proLimit !== null && proLimit !== undefined ? 'limitBodyProLimit' : 'limitBody') : 'limitBodyNoOffer';
  return (
    <div className="own-limit" role="note" data-testid={`own-${kind}-limit`}>
      <span className="own-limit__icon" aria-hidden="true">
        {kind === 'connections' ? <Icons.plug size={17} /> : <Icons.skills size={16} />}
      </span>
      <span className="own-limit__text">
        <strong>{kind === 'skills' && limit === 0 ? t('b2cModels.skills.offTitle', { plan: name }) : t(`b2cModels.${kind}.limitTitle`, { plan: name, limit: value, count: limit })}</strong>
        <span>{t(`b2cModels.${kind}.${body}`, { count: limit, pro: proLimit !== null && proLimit !== undefined ? formatCount(proLimit, i18n.language) : '' })}</span>
      </span>
      {onFree ? (
        <Button className="own-limit__action" onClick={offer.open} data-testid={`own-${kind}-upgrade`}>
          {t('b2cModels.offer.upgrade')}
        </Button>
      ) : null}
    </div>
  );
}
